import React from 'react';
import { useParams } from 'react-router-dom';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { paginateUrl, convertFiltersToQueryString } from '../../helpers/utils';
import { useSelector } from 'react-redux';
import { fetchData } from '../../../../helpers/offthreadFetcher';
import type { RootState } from '../../../../state/store';
import type {
  SummaryItem,
  ReportDataContextType,
  ApiResponse,
  ColumnFilter,
  SelectedColumnFilter,
  SelectedFtsFilter
} from '../../types/report';
import ReportDataContext from '../../contexts/reportDataContext';
import { useFilterNavigate } from '../../../../helpers/hooks/useFilterNavigate';
import DashboardDataContext from '../../../../helpers/contexts/dashboardDataContext';

interface CompanyDetails {
  time_zone_offset: string;
  [key: string]: any;
}

interface CompanyState {
  company_details: CompanyDetails | null;
  shouldUseEpocForTimeRange: boolean;
  [key: string]: any;
}

const fetchReportData = async (endpoint: string): Promise<ApiResponse> => {
  try {
    const response = await fetchData(endpoint, {
      method: 'GET'
    });
    return response as ApiResponse;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

const fetchColumnFilter = async (endpoint: string) => {
  try {
    const response = await fetchData(endpoint, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    });
    return response;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

const fetchSummaryData = async (endpoint: string) => {
  try {
    const response = await fetchData(endpoint, {
      method: 'GET'
    });
    return response;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

interface RouteParams {
  dashboard?: string;
  widget?: string;
  [key: string]: string | undefined;
}

interface QueryParams {
  locationFilters: number[];
  dateFilters: [string, string];
  timeFilters: [[number, number], [number, number]];
  ftsFilters?: SelectedFtsFilter[];
  columnFilters?: SelectedColumnFilter[];
  timezoneOffset: string;
  shouldUseEpocForTimeRange: boolean;
}

const ReportDataProvider = ({ children }: { children: React.ReactNode }) => {
  const routeMatch = useParams<RouteParams>();
  const { dashboardWidgets, dashboardInfo } = useContext(DashboardDataContext);
  const [freezeDateRangeDropdown, setFreezeDateRangeDropdown] = useState(false);
  const [isReportExportEnabled, setIsReportExportEnabled] = useState(false);
  const [isCustomExportEnabled, setIsCustomExportEnabled] = useState(false);

  useEffect(() => {
    if (dashboardWidgets.length > 0 && dashboardInfo) {
      const dashboardWidget = dashboardWidgets.find((d) => d.url === routeMatch.widget);
      if (dashboardWidget) {
        if ('is_timeless_widget' in dashboardWidget) {
          setFreezeDateRangeDropdown(dashboardWidget.is_timeless_widget);
        }
        if ('is_report_export_enabled' in dashboardWidget) {
          setIsReportExportEnabled(dashboardWidget.is_report_export_enabled);
        }
        if ('is_custom_export_enabled' in dashboardWidget) {
          setIsCustomExportEnabled(dashboardWidget.is_custom_export_enabled);
        }
      }
    }
  }, [dashboardWidgets, dashboardInfo]);

  const getBasePath = (params: RouteParams) =>
    params.dashboard && params.widget ? `/api/v1/dar/${params.dashboard}/${params.widget}` : null;

  const buildApiPath = (basePath: string | null, suffix: string = '') =>
    basePath ? `${basePath}${suffix}` : null;

  const baseApiPath = useMemo(() => getBasePath(routeMatch), [routeMatch]);

  const apiPath = useMemo(() => buildApiPath(baseApiPath, '/report'), [baseApiPath]);

  const columnFilterApiPath = useMemo(
    () => buildApiPath(baseApiPath, '/report/dropdown-filters'),
    [baseApiPath]
  );

  const summaryApiPath = useMemo(() => buildApiPath(baseApiPath, '/report/summary'), [baseApiPath]);

  const [reportTitle, setReportTitle] = useState<string>('');
  const [reportData, setReportData] = useState<ReportDataContextType['reportData']>(null);
  const [summaryData, setSummaryData] = useState<SummaryItem[]>([]);
  const [isSummaryLoading, setIsSummaryLoading] = useState<boolean>(false);
  const [currencyCode, setCurrencyCode] = useState<string>('');
  const [marketingHtmlUrl, setMarketingHtmlUrl] = useState<string | null>(null);

  const { filters, company } = useSelector((state: RootState) => ({
    filters: state.filters,
    company: state.company as CompanyState
  }));
  const urlDateValidated = filters.urlDateValidated ?? false;

  const [columnFilterKeys, setColumnFilterKeys] = useState<string[]>([]);
  const [ftsFilterKeys, setFtsFilterKeys] = useState<string[]>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFilter[]>([]);
  const [selectedFtsFilters, setSelectedFtsFilters] = useState<SelectedFtsFilter[]>([]);
  const [selectedColumnFilters, setSelectedColumnFilters] = useState<SelectedColumnFilter[]>([]);

  const [totalPages, setTotalPages] = useState(0);
  const [pageLimit, setPageLimit] = useState({
    startIndex: 1,
    endIndex: 25
  });
  const prevEndpointRef = useRef<string | null>(null);
  // Track total_orders from summary for pagination fallback (used by ClickHouse reports)
  const [summaryTotalOrders, setSummaryTotalOrders] = useState<number | null>(null);

  const [reportConfigs, setReportConfigs] = useState<ReportDataContextType['reportConfigs']>({
    isLoaded: false,
    isLoading: false,
    error: null
  });

  const navigateTo = useFilterNavigate();

  const queryParams = useMemo(() => {
    return {
      locationFilters: filters.locations || [],
      dateFilters: filters.dates as [string, string],
      timeFilters: filters.timeRange as [[number, number], [number, number]],
      ftsFilters: selectedFtsFilters,
      columnFilters: selectedColumnFilters,
      timezoneOffset: company.company_details?.time_zone_offset || '',
      shouldUseEpocForTimeRange: company.shouldUseEpocForTimeRange
    } as QueryParams;
  }, [filters, selectedFtsFilters, selectedColumnFilters, company]);

  const reportDataEndpoint = useMemo(() => {
    if (!apiPath) return null;
    return convertFiltersToQueryString(apiPath, queryParams);
  }, [apiPath, queryParams]);

  const summaryDataEndpoint = useMemo(() => {
    if (!summaryApiPath) return null;
    return convertFiltersToQueryString(summaryApiPath, queryParams);
  }, [summaryApiPath, queryParams]);

  const columnFilterEndpointsList = useMemo(() => {
    if (!columnFilterKeys.length) return [];
    return columnFilterKeys.map((filterKey) => {
      return convertFiltersToQueryString(columnFilterApiPath + `/${filterKey}`, queryParams);
    });
  }, [columnFilterApiPath, columnFilterKeys, queryParams]);

  useEffect(() => {
    if (!urlDateValidated || !reportDataEndpoint) return;

    const endpointChanged =
      prevEndpointRef.current !== null && prevEndpointRef.current !== reportDataEndpoint;
    // If endpoint changed and we're not on page 1, reset pagination and skip this fetch
    if (endpointChanged && pageLimit.startIndex !== 1) {
      prevEndpointRef.current = reportDataEndpoint;
      setPageLimit({ startIndex: 1, endIndex: 25 });
      // Reset totalPages so fallback logic works correctly for new query
      setTotalPages(0);
      setSummaryTotalOrders(null);
      return; // Skip fetch, will re-run with page 1 after state update
    }

    setReportConfigs({
      isLoaded: false,
      isLoading: true,
      error: null
    });
    fetchReportData(paginateUrl(reportDataEndpoint, pageLimit.startIndex, pageLimit.endIndex))
      .then((response) => {
        setReportConfigs({
          isLoaded: true,
          isLoading: false,
          error: null
        });

        setReportData(response.report_data.report_data);
        setCurrencyCode(response.report_data.currency_code || '');
        if (!columnFilterKeys.length) {
          setColumnFilterKeys(response.report_data.dropdown_filters);
        }
        if (!ftsFilterKeys.length) {
          setFtsFilterKeys(response.report_data.fts_filters);
        }
        const reportRowsCount = response.report_data.rows_count;
        if (reportRowsCount != null && reportRowsCount > 0) {
          setTotalPages(Math.ceil(reportRowsCount / 25));
        }
        if (!reportTitle) {
          setReportTitle(response.report_data.title);
        }
        // Update ref after successful processing
        prevEndpointRef.current = reportDataEndpoint;
      })
      .catch((error) => {
        if (error.error && error.lock_type === 'marketing_message') {
          setMarketingHtmlUrl(error.marketing_html_url || '');
          if (error.report_data?.title) {
            setReportTitle(error.report_data.title);
          }
        }
        // TODO: If the error is a 403, show the locked overlay
        setReportConfigs({
          isLoaded: false,
          isLoading: false,
          error: error instanceof Error ? error : new Error(String(error))
        });
      });
  }, [urlDateValidated, reportDataEndpoint, pageLimit]);

  useEffect(() => {
    if (summaryDataEndpoint) {
      setIsSummaryLoading(true);
      fetchSummaryData(summaryDataEndpoint)
        .then((response: any) => {
          setSummaryData(response?.summary);

          // Extract total_orders from summary for pagination fallback (ClickHouse reports)
          const totalOrdersItem = response?.summary?.find(
            (item: SummaryItem) => item.name === 'Total Orders'
          );
          if (totalOrdersItem) {
            setSummaryTotalOrders(Number(totalOrdersItem.value));
          }
        })
        .catch((error) => {
          console.error(error);
        })
        .finally(() => {
          setIsSummaryLoading(false);
        });
    }
  }, [summaryDataEndpoint]);

  // Update totalPages when summary loads (for ClickHouse reports that don't provide rows_count)
  useEffect(() => {
    if (summaryTotalOrders != null && summaryTotalOrders > 0 && totalPages === 0) {
      setTotalPages(Math.ceil(summaryTotalOrders / 25));
    }
  }, [summaryTotalOrders, totalPages]);

  useEffect(() => {
    if (!urlDateValidated || !columnFilterEndpointsList.length) return;

    Promise.all(columnFilterEndpointsList.map(fetchColumnFilter))
      .then((response: any) => {
        setColumnFilters(response.map((res: any) => res.filter_data));
      })
      .catch((error) => {
        console.error(error);
      });
  }, [urlDateValidated, columnFilterEndpointsList]);

  const handlePageChange = (selectedPage: { selected: number }) => {
    const startIndex = selectedPage.selected * 25 + 1;
    setPageLimit({
      startIndex,
      endIndex: startIndex + 25 - 1
    });
  };

  const handleBackNavigation = () => {
    navigateTo(`/${routeMatch.dashboard}/${routeMatch.widget}`);
  };

  const handleColumnFilterChange = (
    name: string,
    filterKey: string,
    selectedValues: { id: string; name: string }[],
    url: string | null
  ) => {
    // Reset pagination to page 1 when filter changes
    setPageLimit({
      startIndex: 1,
      endIndex: 25
    });

    setSelectedColumnFilters((prevFilters) => {
      const existingAppliedFilterIndex = prevFilters.findIndex(
        (filter) => filter.key === filterKey
      );

      if (existingAppliedFilterIndex !== -1) {
        // If the selected values are empty, remove the filter
        if (selectedValues.length === 0) {
          return prevFilters.filter((filter) => filter.key !== filterKey);
        } else {
          // If the selected values are not empty, update the filter
          return prevFilters.map((filter, index) =>
            index === existingAppliedFilterIndex
              ? {
                  ...filter,
                  values: selectedValues.map((value) => ({
                    id: value.id,
                    name: value.name
                  }))
                }
              : filter
          );
        }
      } else {
        // If the filter is not applied, add it
        return [
          ...prevFilters,
          {
            key: filterKey,
            name,
            url: url || filterKey,
            values: selectedValues.map((value) => ({
              id: value.id,
              name: value.name
            }))
          }
        ];
      }
    });
  };

  const removeColumnFilterValue = (filterKey: string, valueId: string) => {
    // Reset pagination to page 1 when filter changes
    setPageLimit({
      startIndex: 1,
      endIndex: 25
    });

    setSelectedColumnFilters((prevFilters) => {
      return prevFilters
        .map((filter) => {
          if (filter.key === filterKey) {
            const updatedValues = filter.values.filter((value) => value.id !== valueId);
            return {
              ...filter,
              values: updatedValues
            };
          }
          return filter;
        })
        .filter((filter) => filter.values.length > 0); // Remove filters with no values
    });
  };

  const resetColumnFilters = () => {
    // Reset pagination to page 1 when filters are cleared
    setPageLimit({
      startIndex: 1,
      endIndex: 25
    });

    setSelectedColumnFilters([]);
  };

  const handleFtsFilterChange = (filterKey: string, value: string) => {
    // Reset pagination to page 1 when filter changes
    setPageLimit({
      startIndex: 1,
      endIndex: 25
    });

    setSelectedFtsFilters((prevFilters) => {
      if (value.trim() === '') {
        // Remove filter if empty value
        return prevFilters.filter((filter) => filter.key !== filterKey);
      }

      if (prevFilters.find((filter) => filter.key === filterKey)) {
        return prevFilters.map((filter) =>
          filter.key === filterKey ? { ...filter, value } : filter
        );
      } else {
        return [...prevFilters, { key: filterKey, value, url: `fts_${filterKey}` }];
      }
    });
  };

  const removeFtsFilter = (filterKey: string) => {
    // Reset pagination to page 1 when filter is removed
    setPageLimit({
      startIndex: 1,
      endIndex: 25
    });

    setSelectedFtsFilters((prevFilters) =>
      prevFilters.filter((filter) => filter.key !== filterKey)
    );
  };

  const resetFtsFilters = () => {
    // Reset pagination to page 1 when filters are cleared
    setPageLimit({
      startIndex: 1,
      endIndex: 25
    });

    setSelectedFtsFilters([]);
  };

  return (
    <ReportDataContext.Provider
      value={
        {
          reportData,
          summaryData,
          isSummaryLoading,
          currencyCode,
          reportConfigs: {
            isLoaded: reportConfigs.isLoaded,
            isLoading: reportConfigs.isLoading,
            error: reportConfigs.error
          },
          totalPages,
          handlePageChange,
          handleBackNavigation,
          pageLimit,
          reportTitle,
          columnFilters,
          handleColumnFilterChange,
          columnFilterKeys,
          ftsFilterKeys,
          selectedColumnFilters,
          resetColumnFilters,
          removeColumnFilterValue,
          handleFtsFilterChange,
          selectedFtsFilters,
          removeFtsFilter,
          resetFtsFilters,
          marketingHtmlUrl,
          freezeDateRangeDropdown,
          isReportExportEnabled,
          isCustomExportEnabled
        } as ReportDataContextType
      }
    >
      {children}
    </ReportDataContext.Provider>
  );
};

export default ReportDataProvider;
